import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencil, faTrash, faSpinner, faScrewdriverWrench } from '@fortawesome/free-solid-svg-icons';
import {
  serviceTypeService,
  type ServiceType,
  type CreateServiceTypePayload,
} from '@/services/service-type.service';
import { SkeletonPage } from '@/components/skeletons';
import { PageHeader } from '@/components/shared/PageHeader';
import { SearchBar } from '@/components/shared/SearchBar';
import { EmptyState } from '@/components/shared/EmptyState';
import { Pagination } from '@/components/shared/Pagination';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast, confirmDelete } from '@/lib/swal';
import { useDebounce } from '@/hooks/use-debounce';
import { useHasPermission } from '@/hooks/use-permissions';

const PAGE_SIZE = 20;

interface ServiceTypeFormProps {
  initial?: Partial<CreateServiceTypePayload>;
  onSubmit: (d: CreateServiceTypePayload) => void;
  onCancel: () => void;
  saving: boolean;
}

function ServiceTypeForm({ initial, onSubmit, onCancel, saving }: ServiceTypeFormProps) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [isBillable, setIsBillable] = useState(initial?.isBillable ?? true);
  const [requiresApproval, setRequiresApproval] = useState(initial?.requiresApproval ?? true);
  const [allowsBerAssessment, setAllowsBerAssessment] = useState(initial?.allowsBerAssessment ?? false);
  const [requiresCommercialReviewDefault, setRequiresCommercialReviewDefault] = useState(
    initial?.requiresCommercialReviewDefault ?? false,
  );
  const [isWarrantyEligible, setIsWarrantyEligible] = useState(initial?.isWarrantyEligible ?? false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string, string> = {};
    if (!code.trim()) errs.code = 'Code is required';
    if (!name.trim()) errs.name = 'Name is required';
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setErrors({});
    onSubmit({
      code: code.trim(),
      name: name.trim(),
      description: description.trim() || undefined,
      isBillable,
      requiresApproval,
      allowsBerAssessment,
      requiresCommercialReviewDefault,
      isWarrantyEligible,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <FormField label="Code" htmlFor="sty-code" required error={errors.code}>
        <Input id="sty-code" value={code} onChange={(e) => setCode(e.target.value)}
          placeholder="e.g. PM, CM, BER" disabled={saving} maxLength={50} />
      </FormField>
      <FormField label="Name" htmlFor="sty-name" required error={errors.name}>
        <Input id="sty-name" value={name} onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Preventive Maintenance" disabled={saving} maxLength={200} />
      </FormField>
      <FormField label="Description" htmlFor="sty-desc">
        <Textarea id="sty-desc" value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description" rows={3} disabled={saving} />
      </FormField>
      <div className="space-y-2">
        <p className="text-sm font-medium text-foreground">Workflow Flags</p>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isBillable} onChange={(e) => setIsBillable(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Billable service (charges apply)
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={requiresApproval} onChange={(e) => setRequiresApproval(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Requires customer approval before proceeding
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={allowsBerAssessment} onChange={(e) => setAllowsBerAssessment(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Allows Beyond Economic Repair (BER) assessment
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={requiresCommercialReviewDefault}
            onChange={(e) => setRequiresCommercialReviewDefault(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Default to commercial review routing for jobs of this type
        </label>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={isWarrantyEligible}
            onChange={(e) => setIsWarrantyEligible(e.target.checked)}
            disabled={saving} className="rounded border-border" />
          Eligible for warranty claim coverage
        </label>
      </div>
      <div className="flex justify-end gap-3 pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button type="submit" disabled={saving} className="min-w-[100px]">
          {saving ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : 'Save'}
        </Button>
      </div>
    </form>
  );
}

export function ServiceTypesPage() {
  const queryClient = useQueryClient();
  const can = useHasPermission();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const [modalOpen, setModalOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<ServiceType | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['service-types', page, debouncedSearch],
    queryFn: () => serviceTypeService.list({ page, pageSize: PAGE_SIZE, search: debouncedSearch || undefined }),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['service-types'] });

  const createMutation = useMutation({
    mutationFn: serviceTypeService.create,
    onSuccess: () => { toast.success('Service type created'); setModalOpen(false); invalidate(); },
    onError: () => toast.error('Failed to create service type'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<CreateServiceTypePayload> }) =>
      serviceTypeService.update(id, payload),
    onSuccess: () => { toast.success('Service type updated'); setModalOpen(false); setEditTarget(null); invalidate(); },
    onError: () => toast.error('Failed to update service type'),
  });

  const deleteMutation = useMutation({
    mutationFn: serviceTypeService.remove,
    onSuccess: () => { toast.success('Service type deleted'); invalidate(); },
    onError: () => toast.error('Failed to delete service type'),
  });

  const handleDelete = async (t: ServiceType) => {
    if (await confirmDelete(`${t.code} — ${t.name}`)) deleteMutation.mutate(t.id);
  };

  const openCreate = () => { setEditTarget(null); setModalOpen(true); };
  const openEdit = (t: ServiceType) => { setEditTarget(t); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setEditTarget(null); };
  const handleSearch = (v: string) => { setSearch(v); setPage(1); };
  const saving = createMutation.isPending || updateMutation.isPending;

  if (isLoading) return <SkeletonPage />;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Service Types"
        description="Categorise the nature of service work — drives billing, approval, and BER routing"
        onAdd={can('service-type:create') ? openCreate : undefined}
        addLabel="Add Service Type"
      />
      <SearchBar value={search} onChange={handleSearch} placeholder="Search by code or name..." />

      {data && data.total > 0 ? (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[900px] text-sm">
              <thead>
                <tr className="border-b border-border bg-muted/40 text-left">
                  <th className="px-4 py-3 font-medium text-foreground">Code</th>
                  <th className="px-4 py-3 font-medium text-foreground">Name</th>
                  <th className="px-4 py-3 font-medium text-foreground">Billable</th>
                  <th className="px-4 py-3 font-medium text-foreground">Approval</th>
                  <th className="px-4 py-3 font-medium text-foreground">BER</th>
                  <th className="px-4 py-3 font-medium text-foreground">Commercial</th>
                  <th className="px-4 py-3 font-medium text-foreground">Warranty</th>
                  <th className="px-4 py-3 font-medium text-foreground">Status</th>
                  <th className="px-4 py-3 font-medium text-foreground sr-only">Actions</th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((t) => (
                  <tr key={t.id} className="border-b border-border last:border-0 hover:bg-muted/20">
                    <td className="px-4 py-3 font-mono font-medium text-foreground">{t.code}</td>
                    <td className="px-4 py-3 font-medium text-foreground">{t.name}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.isBillable ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isBillable ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.requiresApproval ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
                        {t.requiresApproval ? 'Required' : 'None'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.allowsBerAssessment ? 'bg-red-100 text-red-700' : 'bg-muted text-muted-foreground'}`}>
                        {t.allowsBerAssessment ? 'Allowed' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.requiresCommercialReviewDefault ? 'bg-amber-100 text-amber-700' : 'bg-muted text-muted-foreground'}`}>
                        {t.requiresCommercialReviewDefault ? 'Default' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${t.isWarrantyEligible ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isWarrantyEligible ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${t.isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {t.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {can('service-type:update') && (
                          <button onClick={() => openEdit(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
                            aria-label={`Edit ${t.name}`}>
                            <FontAwesomeIcon icon={faPencil} className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('service-type:delete') && (
                          <button onClick={() => handleDelete(t)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                            aria-label={`Delete ${t.name}`} disabled={deleteMutation.isPending}>
                            <FontAwesomeIcon icon={faTrash} className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={data.page} pageSize={data.pageSize} total={data.total} onPageChange={setPage} />
        </>
      ) : (
        <EmptyState
          icon={faScrewdriverWrench}
          title={search ? 'No service types match your search' : 'No service types yet'}
          description={search ? 'Try a different search term.' : 'Add service types like Preventive Maintenance, Corrective Repair, BER Assessment.'}
          action={!search && can('service-type:create') ? <Button onClick={openCreate}>Add Service Type</Button> : undefined}
        />
      )}

      <Modal open={modalOpen} onClose={closeModal} title={editTarget ? 'Edit Service Type' : 'Add Service Type'} size="md">
        <ServiceTypeForm
          initial={editTarget ? { code: editTarget.code, name: editTarget.name, description: editTarget.description ?? undefined, isBillable: editTarget.isBillable, requiresApproval: editTarget.requiresApproval, allowsBerAssessment: editTarget.allowsBerAssessment, requiresCommercialReviewDefault: editTarget.requiresCommercialReviewDefault, isWarrantyEligible: editTarget.isWarrantyEligible } : undefined}
          onSubmit={(p) => editTarget ? updateMutation.mutate({ id: editTarget.id, payload: p }) : createMutation.mutate(p)}
          onCancel={closeModal}
          saving={saving}
        />
      </Modal>
    </div>
  );
}
